import { webPaths } from "@zplanem/web-paths"
import type React from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "../auth-context"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { MobileMoreMenuItem } from "./mobile-more-menu"
import { isPropertyTabRoute } from "./property-tabs"

const FolderIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M2 5a1 1 0 0 1 1-1h3l1.5 1.5H13a1 1 0 0 1 1 1V12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5Z" />
	</svg>
)

const TaskIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M3 4.5h10" />
		<path d="M3 8h10" />
		<path d="M3 11.5h6" />
	</svg>
)

export const MobileBottomNav: React.FC = () => {
	const { logout } = useAuth()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const location = useLocation()
	const navigate = useNavigate()

	// Inside a property's tabbed surface the property-scoped bottom nav
	// (PropertyBottomNav) takes over — Android PropertyBottomBar parity.
	if (isPropertyTabRoute(location.pathname)) return null

	const path = location.pathname
	const propertiesActive =
		path === "/dashboard" ||
		path === "/properties/new" ||
		path.startsWith("/properties/")
	const myTicketsActive = path === webPaths.myTickets()

	return (
		<nav
			aria-label={t.sidebar.navLabel}
			className="md:hidden fixed bottom-0 left-0 right-0 h-16 z-30 bg-card border-t border-border flex items-stretch"
		>
			<Link
				to={webPaths.dashboard()}
				className={
					"flex-1 flex flex-col items-center justify-center gap-1 border-t-2 text-xs transition-colors " +
					(propertiesActive
						? "border-ring bg-accent/40 text-foreground"
						: "border-transparent text-muted-foreground hover:text-foreground")
				}
				aria-current={propertiesActive ? "page" : undefined}
			>
				<FolderIcon />
				<span className="max-w-full truncate px-1">{t.sidebar.properties}</span>
			</Link>

			<Link
				to={webPaths.myTickets()}
				className={
					"flex-1 flex flex-col items-center justify-center gap-1 border-t-2 text-xs transition-colors " +
					(myTicketsActive
						? "border-ring bg-accent/40 text-foreground"
						: "border-transparent text-muted-foreground hover:text-foreground")
				}
				aria-current={myTicketsActive ? "page" : undefined}
			>
				<TaskIcon />
				<span className="max-w-full truncate px-1">{t.sidebar.myTickets}</span>
			</Link>

			<MobileMoreMenuItem
				items={[
					{
						label: t.sidebar.profile,
						onSelect: () => navigate(webPaths.profile()),
					},
					{
						label: t.sidebar.settings,
						onSelect: () => navigate(webPaths.settings()),
					},
					{ label: t.sidebar.signOut, onSelect: logout, tone: "danger" },
				]}
			/>
		</nav>
	)
}
